import type { ReactNode } from 'react';

export type WorkspaceResourceActionPermission = 'manage' | 'read' | 'update';

export type WorkspaceResourceActionResourceType =
    | 'dashboards'
    | 'folders'
    | 'investments'
    | 'lifecycles'
    | 'lists'
    | 'memos'
    | 'pipelines'
    | 'teams';

export type WorkspaceResourceActionKey =
    | 'archived'
    | 'copyLink'
    | 'delete'
    | 'duplicate'
    | 'favorited'
    | 'history'
    | 'locked'
    | 'merge'
    | 'moveToFolder'
    | 'openInNewWindow'
    | 'rename'
    | 'tags';

export type WorkspaceResourceActionMenuItem = {
    children?: WorkspaceResourceActionMenuItem[];
    danger?: boolean;
    disabled?: boolean;
    icon?: ReactNode;
    key: string;
    label: ReactNode;
    onClick?: () => void;
    permission?: WorkspaceResourceActionPermission;
    searchLabel?: string;
    section?: string;
};

export type WorkspaceResourceActionAccess = Record<WorkspaceResourceActionPermission, { disabled: boolean }>;

const trashableWorkspaceResourceTypes = new Set<WorkspaceResourceActionResourceType>([
    'dashboards',
    'folders',
    'investments',
    'lists',
    'memos',
    'pipelines',
]);

/** Keeps reversible Trash actions distinct from immediately destructive deletion. */
export function getWorkspaceResourceRemovalActionLabel(
    resourceType: WorkspaceResourceActionResourceType,
): 'Delete' | 'Move to Trash' {
    return trashableWorkspaceResourceTypes.has(resourceType) ? 'Move to Trash' : 'Delete';
}

const sharedWorkspaceResourceActionKeys: WorkspaceResourceActionKey[] = [
    'favorited',
    'locked',
    'archived',
    'copyLink',
    'openInNewWindow',
    'rename',
    'duplicate',
];

export function getWorkspaceResourceActionKeys(
    resourceType: WorkspaceResourceActionResourceType,
): WorkspaceResourceActionKey[] {
    const keys = sharedWorkspaceResourceActionKeys.filter((key) => key !== 'archived' || resourceType !== 'folders');

    if (!['folders', 'lifecycles', 'teams'].includes(resourceType)) {
        keys.push('moveToFolder');
    }

    keys.push('tags', 'merge', 'history', 'delete');

    return keys;
}

const workspaceResourceActionSectionByKey: Record<string, string> = {
    archived: 'state',
    bannerVisibility: 'state',
    convertToTemplate: 'state',
    copyLink: 'navigation',
    delete: 'danger',
    duplicate: 'mutations',
    edit: 'primary',
    favorited: 'state',
    hidden: 'state',
    history: 'danger',
    hide: 'state',
    locked: 'state',
    tags: 'tags',
    moveToFolder: 'mutations',
    merge: 'danger',
    openInNewWindow: 'navigation',
    publicAuthorVisibility: 'sharing',
    publicCompanyVisibility: 'sharing',
    publicWorkspaceVisibility: 'sharing',
    publicVisibility: 'sharing',
    refresh: 'primary',
    rename: 'mutations',
};

function getWorkspaceResourceActionSection(item: WorkspaceResourceActionMenuItem | undefined) {
    if (item?.section) {
        return item.section;
    }

    const key = typeof item?.key === 'string' ? item.key : undefined;

    return key ? (workspaceResourceActionSectionByKey[key] ?? key) : undefined;
}

export function shouldRenderWorkspaceResourceActionDivider(
    items: WorkspaceResourceActionMenuItem[] | undefined,
    itemIndex: number,
    search: string,
) {
    if (search.trim()) {
        return false;
    }

    const currentSection = getWorkspaceResourceActionSection(items?.[itemIndex]);
    const nextSection = getWorkspaceResourceActionSection(items?.[itemIndex + 1]);

    return Boolean(currentSection && nextSection && currentSection !== nextSection);
}

export function filterWorkspaceResourceActionItems(items: WorkspaceResourceActionMenuItem[], search: string) {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
        return items;
    }

    return items.flatMap((item) => {
        const label = item.searchLabel ?? (typeof item.label === 'string' ? item.label : item.key);
        const matchingChildren = item.children?.filter((child) => {
            const childLabel = child.searchLabel ?? (typeof child.label === 'string' ? child.label : child.key);

            return childLabel.toLowerCase().includes(normalizedSearch);
        });

        if (label.toLowerCase().includes(normalizedSearch)) {
            return [item];
        }

        if (matchingChildren?.length) {
            return [{ ...item, children: matchingChildren }];
        }

        return [];
    });
}

export function getWorkspaceResourceActionItemDisabled(
    item: WorkspaceResourceActionMenuItem,
    access?: WorkspaceResourceActionAccess,
) {
    if (item.disabled) {
        return true;
    }

    if (!access || !item.permission) {
        return false;
    }

    return access[item.permission]?.disabled ?? false;
}
